import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { audienceService } from 'features/services';
import { LoadingStatus, SavingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';

const initialState = {
    audiencesStatus: LoadingStatus.Idle,
    audiences: [],
    loadedAudiencesMap: {},
    audienceUsage: [],
};

export const fetchAudiencesAll = createAsyncThunk('groups/fetchAudiencesPage', async (params) => {
    const response = await audienceService.getAll(params.searchString, params.sort);
    return response;
});

export const getAudienceById = createAsyncThunk('audiences/getAudienceById', async (audienceId) => {
    const response = await audienceService.getById(audienceId);
    response.id = audienceId;
    return response;
});

export const fetchAudienceUsageById = createAsyncThunk(
    'groups/fetchAudienceUsageById',
    async (audienceId) => await audienceService.getAudienceUsageById(audienceId)
);

export const addNewAudience = createAsyncThunk(
    'audiences/addNewAudience',
    async (newAudience) => await audienceService.create(newAudience)
);

export const editAudience = createAsyncThunk(
    'audiences/editAudience',
    async (updates) => await audienceService.update(updates.audienceId, updates.audienceUpdates)
);

export const deleteAudienceById = createAsyncThunk('audiences/deleteAudienceById', async (audienceId) => {
    const response = await audienceService.delete(audienceId);
    response.id = audienceId;
    return response;
});

export const deleteAudienceArray = createAsyncThunk('audiences/deleteAudienceArray', async (audienceIds) => {
    const response = await audienceService.deleteArray(audienceIds);
    response.id = audienceIds;
    return response;
});

export const addRule = createAsyncThunk('audiences/addRule', async (params) => {
    const response = await audienceService.addRule(params);
    return response;
});

export const removeRule = createAsyncThunk('audiences/removeRule', async (params) => {
    const response = await audienceService.removeRule(params);
    return response;
});

const audiencesSlice = createSlice({
    name: 'audiences',
    initialState,
    reducers: {
        resetAudienceState: (state) => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAudiencesAll.pending, (state, action) => {
                state.audiencesStatus = LoadingStatus.Loaded;
            })
            .addCase(fetchAudiencesAll.fulfilled, (state, action) => {
                state.audiencesStatus = LoadingStatus.Loaded;
                state.audiencesPageInfo = action.payload.pageInfo;
                state.audiences = action.payload.audiences;

                const startIndex = (action.meta.arg.page - 1) * action.meta.arg.pageSize;
                const stopIndex = startIndex + state.audiences.length - 1;
                const requestedStopIndex = startIndex + action.meta.arg.pageSize - 1;

                state.loadedAudiencesMap = {};

                for (var i = startIndex; i <= stopIndex; i++) {
                    state.loadedAudiencesMap[i] = LoadingStatus.Loaded;
                }

                // In case we didn't load as many as requested
                for (var j = stopIndex + 1; j <= requestedStopIndex; j++) {
                    delete state.loadedAudiencesMap[j];
                }
            })
            .addCase(fetchAudiencesAll.rejected, (state, action) => {
                state.audiencesStatus = LoadingStatus.Failed;
            })
            .addCase(getAudienceById.fulfilled, (state, action) => {})
            .addCase(addNewAudience.fulfilled, (state, action) => {
                state.audiencesStatus = LoadingStatus.Idle;
            })
            .addCase(editAudience.fulfilled, (state, action) => {
                state.audiencesStatus = LoadingStatus.Idle;
            })
            .addCase(deleteAudienceById.fulfilled, (state, action) => {
                state.audiencesStatus = LoadingStatus.Idle;
            })
            .addCase(fetchAudienceUsageById.fulfilled, (state, action) => {
                state.audienceUsage = action.payload;
            })
            .addCase(deleteAudienceArray.fulfilled, (state, action) => {
                state.audiencesStatus = LoadingStatus.Idle;
            })
            .addCase(deleteAudienceArray.rejected, (state, action) => {
                state.status = SavingStatus.Failed;
                state.error = action.error.message;
            });
    },
});

export const selectAllAudiences = (state) => state.audiences.audiences;
export const getAudiencePageInfo = (state) => state.audiences.audiencePageInfo;
export const getAudienceStatus = (state) => state.audiences.audiencesStatus;
export const getAudienceUsage = (state) => state.audiences.audienceUsage;

export const { resetAudienceState } = audiencesSlice.actions;

export default audiencesSlice.reducer;
